import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { switchMap, map, catchError, withLatestFrom } from 'rxjs/operators';

import { GroupsService } from '../../../core/services/groups.service';
import * as GroupsActions from './groups.actions';
import { selectGroupsMeta, selectGroupsLastQuery } from './groups.selectors';
import { DEFAULT_GROUPS_CURRENT_PAGE, DEFAULT_GROUPS_PER_PAGE } from '../models/group.model';

@Injectable()
export class GroupsEffects {
  private actions$ = inject(Actions);
  private groupsService = inject(GroupsService);
  private store = inject(Store);

  loadGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GroupsActions.loadGroups),
      switchMap((action) =>
        this.groupsService
          .getGroupsList({
            page: action.page ?? DEFAULT_GROUPS_CURRENT_PAGE,
            per_page: action.perPage ?? DEFAULT_GROUPS_PER_PAGE,
            search: action.search ?? '',
            sort: action.sort ?? 'created_at',
            sort_direction: action.sortDirection ?? 'desc',
            filters: action.filters,
            filter_link: action.filterLink,
          })
          .pipe(
            map((response) =>
              GroupsActions.loadGroupsSuccess({
                groups: response.data,
                meta: response.meta,
              })
            ),
            catchError((error) =>
              of(GroupsActions.loadGroupsFailure({ error: error?.message ?? 'Failed to load groups' }))
            )
          )
      )
    )
  );

  submitGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GroupsActions.submitGroup),
      switchMap((action) => {
        const { payload, isEditMode } = action;
        const request$ = isEditMode && payload.uuid
          ? this.groupsService.updateGroup(payload.uuid, payload)
          : this.groupsService.createGroup(payload);

        return request$.pipe(
          map(() => GroupsActions.submitGroupSuccess()),
          catchError((error) =>
            of(GroupsActions.submitGroupFailure({ error: error?.message ?? 'Failed to save group' }))
          )
        );
      })
    )
  );

  deleteGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GroupsActions.deleteGroup),
      switchMap((action) =>
        this.groupsService.deleteGroup(action.groupId).pipe(
          map(() => GroupsActions.deleteGroupSuccess()),
          catchError((error) =>
            of(GroupsActions.deleteGroupFailure({ error: error?.message ?? 'Failed to delete group' }))
          )
        )
      )
    )
  );

  bulkDeleteGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GroupsActions.bulkDeleteGroups),
      switchMap(({ uuids, allSelected }) =>
        this.groupsService.bulkDeleteGroups({ uuids, allSelected }).pipe(
          map(() => GroupsActions.bulkDeleteGroupsSuccess()),
          catchError((error) =>
            of(
              GroupsActions.bulkDeleteGroupsFailure({
                error: error?.message ?? 'Failed to delete groups',
              })
            )
          )
        )
      )
    )
  );

  reloadAfterMutation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        GroupsActions.submitGroupSuccess,
        GroupsActions.deleteGroupSuccess,
        GroupsActions.bulkDeleteGroupsSuccess
      ),
      withLatestFrom(
        this.store.select(selectGroupsMeta),
        this.store.select(selectGroupsLastQuery)
      ),
      map(([, meta, lastQuery]) =>
        GroupsActions.loadGroups({
          page: meta?.currentPage ?? DEFAULT_GROUPS_CURRENT_PAGE,
          perPage: meta?.perPage ?? DEFAULT_GROUPS_PER_PAGE,
          search: lastQuery.search,
          filters: lastQuery.filters,
          filterLink: lastQuery.filterLink,
          sort: lastQuery.sort,
          sortDirection: lastQuery.sortDirection,
        })
      )
    )
  );
}
